import React, { useContext, useState } from 'react';

import { CheckCircleOutlined, ClockCircleOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { PermissionsContext } from 'context';
import { useTranslation } from 'react-i18next';

import { StyledModal } from 'components/Modals/index';
import { PrimaryButton } from 'components/UI';
import {
  ForecastBlockerCode,
  PermissionsEnum,
  useGetForecastCapabilitiesQuery,
  RequestSalesForecastRefreshMutation,
  useRequestSalesForecastRefreshMutation,
} from 'services/graphql/main';
import { useError } from 'services/utils';

import { BoxTitle, DetailsBox, SubTitle } from '../styles';
import { RefreshBar, RefusalContent } from './styles';

type Blocker = NonNullable<RequestSalesForecastRefreshMutation['requestSalesForecastRefresh']['blockers']>[number];

/**
 * Triggers today's calculation. Same condition as the automated chain: the point is a corrected
 * file. Labels are composed here, from the codes the server returns.
 */
const ForecastRefreshBox: React.FC = () => {
  const { t } = useTranslation('translation');
  const { addError } = useError();
  const { permissions } = useContext(PermissionsContext);

  const { data: dataCapabilities } = useGetForecastCapabilitiesQuery({
    onError: (err) => addError(err, 'error'),
  });

  const hasSalesForecast = !!dataCapabilities?.forecastCapabilities?.hasSalesForecast;
  const hasAutoSizeGrid = !!dataCapabilities?.forecastCapabilities?.hasAutoSizeGrid;

  // A tenant that gets neither has nothing to recompute; one that gets a single offer keeps the
  // box, with its wording stripped of what it does not receive.
  let suffix = '';

  if (!hasSalesForecast) suffix = '_grid_only';
  else if (!hasAutoSizeGrid) suffix = '_forecast_only';

  const [isVerdictOpen, setIsVerdictOpen] = useState(false);

  const [request, { loading, data }] = useRequestSalesForecastRefreshMutation({
    onError: (err) => addError(err, 'error'),
    // On onCompleted rather than an effect: a second click with the same verdict must reopen
    // the dialog, yet the response would be identical and the effect would not fire.
    onCompleted: () => setIsVerdictOpen(true),
  });

  const result = data?.requestSalesForecastRefresh;

  const entityName = (entity?: string | null) => {
    switch (entity) {
      case 'Sale':
        return t('productOptimalStock.sales_forecast.entity_sale');
      case 'StoreStock':
        return t('productOptimalStock.sales_forecast.entity_store_stock');
      case 'WarehouseStock':
        return t('productOptimalStock.sales_forecast.entity_warehouse_stock');
      default:
        return entity ?? '';
    }
  };

  /** The problem alone: the offending file is rendered separately, so it can be scanned. */
  const problem = (blocker: Blocker) => {
    switch (blocker.code) {
      case ForecastBlockerCode.NoImport:
        return t('productOptimalStock.sales_forecast.problem_no_import');
      case ForecastBlockerCode.ImportFailed:
        return t('productOptimalStock.sales_forecast.problem_import_failed');
      case ForecastBlockerCode.NoRows:
        return t('productOptimalStock.sales_forecast.problem_no_rows');
      case ForecastBlockerCode.AlreadyRequested:
        return t('productOptimalStock.sales_forecast.blocker_already_requested');
      case ForecastBlockerCode.ErrorRate: {
        const { rowsInError, rowsRead } = blocker;
        // Two counters rather than a percentage: formatting depends on the locale.
        const rate = rowsRead ? ((rowsInError ?? 0) / rowsRead) * 100 : 0;

        return t('productOptimalStock.sales_forecast.problem_error_rate', {
          rate: rate.toFixed(1),
          rowsInError,
          rowsRead,
        });
      }
      default:
        return '';
    }
  };

  // « A calculation is already running » calls for no correction: serving it the faulty-file
  // wording would send the user hunting for a problem that does not exist.
  const isAccepted = result?.isAccepted === true;

  const isAlreadyRunning =
    result?.blockers?.length === 1 && result.blockers[0].code === ForecastBlockerCode.AlreadyRequested;

  // Neither offer: the button would trigger a chain that produces nothing for this tenant.
  if (!hasSalesForecast && !hasAutoSizeGrid) return null;

  // The mutation is reserved to Admin and Editor: showing a Viewer a button that can only be
  // refused is worse than showing nothing.
  if (!(permissions as PermissionsEnum[])?.includes(PermissionsEnum.DashboardForecastRefreshEdit)) return null;

  return (
    <>
      <DetailsBox>
        <BoxTitle>{t(`productOptimalStock.sales_forecast.refresh_box_title${suffix}`)}</BoxTitle>
        <SubTitle>{t(`productOptimalStock.sales_forecast.refresh_hint${suffix}`)}</SubTitle>

        <RefreshBar>
          <PrimaryButton
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => request()}
            textKey={`productOptimalStock.sales_forecast.refresh${suffix}`}
          />
        </RefreshBar>
      </DetailsBox>

      <StyledModal
        open={isVerdictOpen}
        onCancel={() => setIsVerdictOpen(false)}
        onOk={() => setIsVerdictOpen(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText={t('common.close')}
        title={
          <>
            {isAccepted && <CheckCircleOutlined />}
            {!isAccepted && isAlreadyRunning && <ClockCircleOutlined />}
            {!isAccepted && !isAlreadyRunning && <WarningOutlined />}{' '}
            {isAccepted
              ? t('productOptimalStock.sales_forecast.refresh_accepted_title')
              : isAlreadyRunning
              ? t('productOptimalStock.sales_forecast.refresh_running_title')
              : t('productOptimalStock.sales_forecast.refresh_refused_title')}
          </>
        }
      >
        <RefusalContent>
          {isAccepted ? (
            <p>{t(`productOptimalStock.sales_forecast.refresh_accepted${suffix}`)}</p>
          ) : isAlreadyRunning ? (
            <p>{t(`productOptimalStock.sales_forecast.refresh_running_body${suffix}`)}</p>
          ) : (
            <>
              <p>{t('productOptimalStock.sales_forecast.refresh_refused_files')}</p>

              <ul>
                {result?.blockers?.map((blocker) => (
                  <li key={`${blocker.entity ?? 'x'}-${blocker.code}`}>
                    {blocker.entity && <b>{entityName(blocker.entity)}</b>}
                    <span>{problem(blocker)}</span>
                  </li>
                ))}
              </ul>

              <p className="hint">
                {t('productOptimalStock.sales_forecast.refresh_refused_hint')}
                <span className="threshold">{t('productOptimalStock.sales_forecast.refresh_threshold')}</span>
              </p>
            </>
          )}
        </RefusalContent>
      </StyledModal>
    </>
  );
};

export default ForecastRefreshBox;
